import React, { useCallback, useEffect, useState } from "react";
import { Grid3x3 } from "lucide-react";
import api from "@/lib/api";
import { RecruitDialog } from "@/components/admin/RecruitDialog";

const STYLES = {
  missing: "bg-[#B91C1C]/10 text-[#B91C1C] ring-1 ring-inset ring-[#B91C1C]/30",
  thin: "bg-[#E9C46A]/25 text-[#8A5A00]",
  ok: "bg-[#2D6A4F]/15 text-[#2D6A4F]",
  none: "text-[#B8B2A3]",
};

const LEGEND = [
  { key: "missing", label: "Missing: requested, 0 collectors" },
  { key: "thin", label: "Thin: only 1 collector" },
  { key: "ok", label: "Covered: 2+ collectors" },
];

const Cell = ({ cell, testid, stat, onRecruit }) => {
  const missing = cell.status === "missing";
  const Box = missing ? "button" : "div";
  return (
    <td className="px-1.5 py-1.5 align-top" data-testid={testid}>
      <Box type={missing ? "button" : undefined} onClick={missing ? onRecruit : undefined} data-testid={missing ? `recruit-btn-${testid.slice(9)}` : undefined}
        className={`block w-full rounded-md px-2 py-1.5 text-center min-w-[64px] ${STYLES[cell.status]} ${missing ? "transition-colors hover:bg-[#B91C1C]/20 cursor-pointer" : ""}`}
        title={`${cell.requests} request(s) · ${cell.collectors} collector(s) nearby${stat ? ` · Invited ${stat.invited} · ${stat.converted} signed up/added service` : ""}`}>
        <div className="text-sm font-semibold leading-none">{cell.requests || "—"}</div>
        <div className="text-[10px] mt-1 leading-none opacity-80">{cell.collectors} col</div>
        {missing && <div className="text-[10px] mt-1 font-semibold underline underline-offset-2 leading-none">Recruit</div>}
      </Box>
      {stat && <div className="text-[9px] text-[#5C6B64] mt-1 text-center whitespace-nowrap" data-testid={`recruit-stat-${testid.slice(9)}`}>{stat.invited} inv · {stat.converted} joined</div>}
    </td>
  );
};

export const ServiceCoverageGrid = ({ data }) => {
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [stats, setStats] = useState({});
  const [target, setTarget] = useState(null);
  const loadStats = useCallback(() => { api.get("/admin/recruit/stats").then(({ data }) => setStats(data.cells || {})).catch(() => {}); }, []);
  useEffect(() => { loadStats(); }, [loadStats]);
  const closeDialog = useCallback(() => setTarget(null), []);
  const services = data?.services || [];
  const rows = (data?.rows || []).filter((r) => !onlyMissing || r.missing > 0);
  return (
    <div className="mt-8" data-testid="admin-service-coverage">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
        <div>
          <h2 className="text-lg font-heading text-[#1E3F33] flex items-center gap-2"><Grid3x3 className="w-5 h-5 text-[#CC5A3A]" /> Service coverage — ZIP × service</h2>
          <p className="text-sm text-[#8A9A86] mt-1">Busiest ZIPs: requests per service (top number) vs. lead-eligible collectors nearby offering it (bottom). Click a red cell to recruit collectors there.</p>
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-[#5C6B64] cursor-pointer">
          <input type="checkbox" data-testid="service-coverage-only-missing" checked={onlyMissing} onChange={(e) => setOnlyMissing(e.target.checked)} className="rounded border-[#1E3F33]/30 text-[#2D6A4F]" />
          Only ZIPs with missing services
        </label>
      </div>
      <div className="flex flex-wrap gap-3 mb-3" data-testid="service-coverage-legend">
        {LEGEND.map((l) => <span key={l.key} className="inline-flex items-center gap-1.5 text-xs text-[#5C6B64]"><span className={`w-3 h-3 rounded-sm ${STYLES[l.key]}`} />{l.label}</span>)}
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-[#8A9A86]" data-testid="service-coverage-empty">{onlyMissing ? "No ZIPs with missing services in this period." : "No request data in this period."}</p>
      ) : (
        <div className="overflow-auto max-h-[28rem] rounded-xl border border-[#1E3F33]/10 bg-white">
          <table className="w-full text-sm border-separate border-spacing-0" data-testid="service-coverage-table">
            <thead className="bg-[#F0EFEA] text-[#1E3F33] sticky top-0 z-10"><tr>
              <th className="px-3 py-2.5 text-left font-medium sticky left-0 bg-[#F0EFEA] z-10">ZIP</th>
              <th className="px-3 py-2.5 text-left font-medium">Requests</th>
              {services.map((s) => <th key={s.code} className="px-1.5 py-2.5 text-[11px] font-medium leading-tight text-center whitespace-pre-line">{s.label.replace(/ · /g, "\n")}</th>)}
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.zip} className="border-t border-[#1E3F33]/8" data-testid={`service-coverage-row-${r.zip}`}>
                  <td className="px-3 py-2 sticky left-0 bg-white">
                    <div className="font-medium text-[#1E3F33]">{r.zip}</div>
                    <div className="text-[11px] text-[#8A9A86]">{r.state}{r.missing ? <span className="text-[#B91C1C] font-medium"> · {r.missing} missing</span> : null}</div>
                  </td>
                  <td className="px-3 py-2 text-[#5C6B64]">{r.requests}</td>
                  {services.map((s) => <Cell key={s.code} cell={r.cells[s.code]} testid={`svc-cell-${r.zip}-${s.code}`} stat={stats[`${r.zip}|${s.code}`]} onRecruit={() => setTarget({ zip: r.zip, code: s.code, label: s.label })} />)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <RecruitDialog target={target} onClose={closeDialog} onSent={loadStats} />
    </div>
  );
};
